import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {IDialogView} from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import {DialogModel} from '@shared/models/dialog.model';
import {combineLatest, Observable, Subject} from 'rxjs';
import {TranslationService} from '../../../services/translation.service';
import {TranslationSupportiveLanguage} from '../../../models/translation.model';
import {SnackBarService} from '@core/services/snack-bar.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {MatDialogRef} from '@angular/material/dialog';
import {FileManagementService} from '@core/services/file-management.service';
import {takeUntil} from 'rxjs/operators';
import {QueryProvider} from '@shared/queries/query-provider';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {select, Store} from '@ngrx/store';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import {StorageSettings} from '../../../models/translation.constants';
import {hasFeature} from '@shared/store/selectors/security.selector';
import {SecurityState} from '@shared/store/reducers/security.reducer';

@Component({
    standalone: false,
    selector: 'particle-translation-compare-dialog',
  templateUrl: './translation-compare-dialog.component.html',
  styleUrls: ['./translation-compare-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TranslationCompareDialogComponent implements OnInit, IDialogView, OnDestroy {
  data: DialogModel;
  fileToUploadCompareFrom: File;
  fileToUploadCompareTo: File;
  isFileScannedFrom: boolean;
  isFileScannedTo: boolean;
  unsubscribe$ = new Subject<void>();
  taxformsSupportiveLanguage: TranslationSupportiveLanguage[] = [];
  private language$: any;
  isCheckFileFormat = true;
  fromLanguage: string;
  toLanguage: string;
  supportiveLanguageQueryKey = 'TranslationSupportiveLanguage';
  translationId: string;
  storageSettings = 'Stored';
  storageOptions = StorageSettings;
  hasFeature$: Observable<any>;
  hasKPMGFeature: boolean = false;

  constructor(private translationService: TranslationService,
              private snackBarService: SnackBarService,
              private fileManagementService: FileManagementService,
              private queryStore: Store<IQueryState>,
              private fuseSplashScreenService: FuseSplashScreenService,
              public dialogRef: MatDialogRef<TranslationCompareDialogComponent>,
              private securityStore: Store<SecurityState>) {
    this.isFileScannedFrom = false;
    this.isFileScannedTo = false;
    this.fromLanguage = '';
    this.toLanguage = '';
    this.fileToUploadCompareFrom = null;
    this.fileToUploadCompareTo = null;
  }
  ngOnInit() {
    this.translationId = this.data.input.translationId;

    this.hasFeature$ = this.securityStore.pipe(select(hasFeature));

    this.hasFeature$.pipe(takeUntil(this.unsubscribe$)).subscribe(x => {
      this.hasKPMGFeature = x('KPMG');
      if (this.hasKPMGFeature) {
        this.storageSettings = StorageSettings.NonStored;
      } else {
        this.storageSettings = StorageSettings.Stored;
      }
    });

    this.language$ = this.queryStore.pipe(select(getQueryResponse, this.supportiveLanguageQueryKey), takeUntil(this.unsubscribe$));
    this.language$.subscribe(language => {
      if (language) {
        this.taxformsSupportiveLanguage = language;
        if (this.taxformsSupportiveLanguage && this.taxformsSupportiveLanguage.length > 0) {
          this.taxformsSupportiveLanguage = this.translationService.sortTranslationSupportiveLanguage(this.taxformsSupportiveLanguage);
        }
      }
    });
    QueryProvider.invokeQuery(this.queryStore, this.supportiveLanguageQueryKey, {});
  }

  handleFileInputFrom() {
    const expectedType: any[] = ['.pdf'];
    const fileFormatOutput = this.fileManagementService.handleFileInput(
      this.fileToUploadCompareFrom,
      expectedType
    );
    if (fileFormatOutput) {
      this.isCheckFileFormat = true;
    } else {
      this.isCheckFileFormat = false;
      this.fileToUploadCompareFrom = null;
    }
  }

  handleFileInputTo() {
    const expectedType: any[] = ['.pdf'];
    const fileFormatOutput = this.fileManagementService.handleFileInput(
      this.fileToUploadCompareTo,
      expectedType
    );
    if (fileFormatOutput) {
      this.isCheckFileFormat = true;
    } else {
      this.isCheckFileFormat = false;
      this.fileToUploadCompareTo = null;
    }
  }

  onOkClicked(): boolean {
    if (this.fileToUploadCompareFrom === null) {
      this.snackBarService.showError('Please select document 1.');
      return false;
    }
    if (this.fileToUploadCompareTo === null) {
      this.snackBarService.showError('Please select document 2.');
      return false;
    }
    if (this.isFileScannedFrom && !this.fromLanguage) {
      this.snackBarService.showError('Please select language for document 1.');
      return false;
    }
    if (this.isFileScannedTo && !this.toLanguage) {
      this.snackBarService.showError('Please select language for document 2.');
      return false;
    }
    if (this.translationService.isOcrNotSupportiveLanguageSelected(this.fromLanguage) || this.translationService.isOcrNotSupportiveLanguageSelected(this.toLanguage)) {
      this.snackBarService.showError('The language in the scanned document you are uploading is not supported by our OCR engine. Please upload an electronic version of the document.');
      return false;
    }

    this.data.input.formConfig = {
      form1: {
        action: 'compare',
        isFileScanned: this.isFileScannedFrom,
        language: this.fromLanguage,
        fileName: this.fileToUploadCompareFrom.name
        // file: this.fileToUploadCompareFrom,
        // fileId: this.fileIdByUploadingFrom
      },
      form2: {
        action: 'compare',
        isFileScanned: this.isFileScannedTo,
        language: this.toLanguage,
        fileName:  this.fileToUploadCompareTo.name
        // file: this.fileToUploadCompareTo,
        // fileId: this.fileIdByUploadingTo
      },
      storageSettings: this.storageSettings,
      storageOptions: this.storageOptions
    };
    if (this.fileToUploadCompareFrom && this.fileToUploadCompareTo) {
      this.fileUploader();
      return false;
    }
    return false;
  }
  fileUploader() {
    this.fuseSplashScreenService.show();
    const combinedData$ = combineLatest([this.translationService.uploadClientFile(this.fileToUploadCompareFrom, this.translationId).pipe(takeUntil(this.unsubscribe$)),
      this.translationService.uploadClientFile(this.fileToUploadCompareTo, this.translationId).pipe(takeUntil(this.unsubscribe$))]);
    let hasData = false;
    combinedData$.subscribe((result: any[]) => {
      if (!hasData && result && result[0] && result[1]) {
        hasData = true;
        this.fuseSplashScreenService.hide();
        console.log('formconfig;', result);
        this.data.input.formConfig.form1.fileId = result[0];
        this.data.input.formConfig.form2.fileId = result[1];
        this.dialogRef.close({action: 'ok', data: this.data});
      }
    });
  }

  ngOnDestroy() {
  }

}
